// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  Method,
  getAssetPath,
  State
} from "@stencil/core";

// Other Libraries
import { MercuryBundles } from "@genexus/mercury";

// Custom Imports
import { Locale } from "../../../common/locale";
import { RadioGroupItemModel } from "@genexus/chameleon-controls-library/dist/types/components/radio-group/types";
import { stringIsNumber } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form",
  "utils/layout",
  "utils/spacing",
  "utils/typography",
  "components/radio-group",
  "components/button",
  "components/edit"
];

const RD_BTN_LATEST_REVISION_VALUE = "latestRevision";
const RD_BTN_UPDATE_TO_REVISION_VALUE = "updateToRevision";

@Component({
  tag: "gx-ide-team-dev-update-to-revision",
  styleUrl: "update-to-revision.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/team-dev-update-to-revision"]
})
export class GxIdeTeamDevUpdateToRevision {
  #_componentLocale: any;
  #chShortcutsEl: HTMLChShortcutsElement;
  #radioGroupOptions: RadioGroupItemModel[] = [
    { value: RD_BTN_LATEST_REVISION_VALUE, caption: "" },
    { value: RD_BTN_UPDATE_TO_REVISION_VALUE, caption: "" }
  ];
  #renderedFirstTime = false;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/team-dev-update-to-revision/shortcuts.json`
  );

  @Element() el: HTMLGxIdeTeamDevUpdateToRevisionElement;

  #radioGroupEl!: HTMLChRadioGroupRenderElement;
  #revisionNumberEl!: HTMLChEditElement;

  /**
   * To check if the status of the information is valid
   */
  @State() isValid: boolean;

  /**
   * This is a function provided by the developer for execute when cancel button is pressed.
   */
  @Prop() readonly cancelCallback!: cancelCallbackFn;

  /**
   * This is a function provided by the developer and is invoked when the action confirm is executed passing the selected objects of the grid
   */
  @Prop() readonly confirmCallback!: confirmCallbackFn;

  /**
   * Set an initial value for the review
   */
  @Prop() readonly currentRevision: string | undefined;

  /**
   * This event is emitted once just after the component is fully loaded and the first render() occurs.
   */
  @Event() componentDidLoadEvent: EventEmitter<boolean>;

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  componentDidLoad() {
    this.componentDidLoadEvent.emit(true);
    this.#setInitialCheckedRadio();
  }

  componentDidRender() {
    if (!this.#renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this.#_componentLocale.componentName
      );
      this.#renderedFirstTime = true;
    }

    this.#radioGroupOptions.find(
      rg => rg.value === RD_BTN_LATEST_REVISION_VALUE
    ).caption = this.#_componentLocale.options.updateToLatest;

    this.#radioGroupOptions.find(
      rg => rg.value === RD_BTN_UPDATE_TO_REVISION_VALUE
    ).caption = this.#_componentLocale.options.updateToRevision;
  }

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
    this.isValid = true;
  }

  /**
   * Validate necessary data input
   */
  @Method()
  async validate(): Promise<boolean> {
    const isValid = true;
    return isValid;
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  #cancel = (): void => {
    this.cancelCallback();
  };

  #confirm = (): void => {
    let revision = undefined;
    if (this.#radioGroupEl.value === RD_BTN_UPDATE_TO_REVISION_VALUE) {
      revision = this.#revisionNumberEl.value;
    }
    this.confirmCallback(revision);
  };

  #inputChangeHandler = (event: CustomEvent<string> | InputEvent) => {
    this.isValid = stringIsNumber(event.detail);
  };

  #radioGroupChangeHandler = (event: CustomEvent<string>) => {
    this.#revisionNumberEl.disabled =
      event.detail !== RD_BTN_UPDATE_TO_REVISION_VALUE;
    this.isValid = true;
    if (event.detail === RD_BTN_LATEST_REVISION_VALUE) {
      this.isValid = true;
    } else if (
      event.detail === RD_BTN_UPDATE_TO_REVISION_VALUE &&
      !stringIsNumber(this.#revisionNumberEl.value)
    ) {
      this.isValid = false;
      setTimeout(() => {
        this.#revisionNumberEl.focus();
      }, 2000);
    }
  };

  #renderFooter = (): Element => {
    return (
      <footer class="footer dialog-footer spacing-body-block-start">
        <div class="buttons-spacer">
          <button
            class="button-secondary"
            onClick={this.#cancel}
            part="button-cancel"
          >
            {this.#_componentLocale.footer.btnCancel}
          </button>

          <button
            class="button-primary"
            onClick={this.#confirm}
            part="button-update"
            disabled={!this.isValid}
          >
            {this.#_componentLocale.footer.btnUpdate}
          </button>
        </div>
      </footer>
    );
  };

  #setInitialCheckedRadio = () => {
    if (this.currentRevision === undefined) {
      this.#radioGroupEl.value = RD_BTN_LATEST_REVISION_VALUE;
      this.isValid = true;
      this.#revisionNumberEl.disabled = true;
    } else {
      this.#revisionNumberEl.disabled = false;
      this.#revisionNumberEl.value = this.currentRevision;
      this.#radioGroupEl.value = RD_BTN_UPDATE_TO_REVISION_VALUE;
    }
  };

  render(): void {
    return (
      <Host class="widget scrollable field-group spacing-body">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>

        <section class="main">
          <ch-radio-group-render
            class="radio-group"
            model={this.#radioGroupOptions}
            direction="horizontal"
            onChange={this.#radioGroupChangeHandler}
            ref={(el: HTMLChRadioGroupRenderElement) => {
              this.#radioGroupEl = el as HTMLChRadioGroupRenderElement;
            }}
          ></ch-radio-group-render>

          <ch-edit
            autoFocus
            class="input revision-number"
            onInput={this.#inputChangeHandler}
            ref={(el: HTMLChEditElement) =>
              (this.#revisionNumberEl = el as HTMLChEditElement)
            }
          ></ch-edit>

          {this.#renderFooter()}
        </section>
      </Host>
    );
  }
}

export type cancelCallbackFn = () => Promise<void>;
export type confirmCallbackFn = (revision: string | undefined) => Promise<void>;
